import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import CustomModal from "../../../common/customModal/CustomModal";
import { AdminTable, ActionCell } from "../../../common/adminTable";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { fetchCategories, createCategory, updateCategory, deleteCategory } from "../categorySlice";
import { inputSx } from "../blogTheme";

const EMPTY_FORM = { name: "", description: "", parent: "", color: "#000000" };

export default function CategoryList() {
  const dispatch = useDispatch();
  const { categories, loading, error } = useSelector((state) => state.category);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { dispatch(fetchCategories()); }, [dispatch]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      description: cat.description || "",
      parent: cat.parent?._id || "",
      color: cat.color || "#000000",
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    const data = { name: form.name, description: form.description, color: form.color };
    if (form.parent) data.parent = form.parent;
    if (editing) dispatch(updateCategory({ id: editing._id, data }));
    else dispatch(createCategory(data));
    setDialogOpen(false);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete Category",
      description: "Are you sure you want to delete this category?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deleteCategory(id));
    setDeletingId(null);
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 160,
      renderCell: (cat) => cat.name,
    },
    {
      field: "slug",
      headerName: "Slug",
      minWidth: 140,
      cellSx: { opacity: 0.6 },
      renderCell: (cat) => cat.slug || "—",
    },
    {
      field: "postCount",
      headerName: "Posts",
      width: 80,
      renderCell: (cat) => cat.postCount ?? 0,
    },
    {
      field: "parent",
      headerName: "Parent",
      width: 140,
      renderCell: (cat) => cat.parent?.name || "—",
    },
    {
      field: "color",
      headerName: "Color",
      width: 80,
      renderCell: (cat) => (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            bgcolor: cat.color || "transparent",
            border: "1px solid #EBEBEB",
          }}
        />
      ),
    },
    {
      field: "__actions",
      headerName: "",
      width: 90,
      align: "right",
      renderCell: (cat) => (
        <ActionCell
          actions={[
            {
              label: "Edit",
              icon: <EditIcon fontSize="small" />,
              onClick: () => openEdit(cat),
            },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDelete(cat._id),
              loading: deletingId === cat._id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: "Categories" }]} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>
          Categories
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
            color: "#fff",
            borderRadius: "29px",
            boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
            fontFamily: "Heebo",
            textTransform: "none",
            px: 3,
            "&:hover": { opacity: 0.9 },
          }}
        >
          Add Category
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <AdminTable
        columns={columns}
        rows={categories}
        loading={loading}
        rowKey="_id"
        emptyText="No categories found"
        deletingId={deletingId}
      />

      {/* Create / Edit dialog — logic unchanged */}
      <CustomModal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #F0F0F0" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>
            {editing ? "Edit Category" : "New Category"}
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            size="small"
            fullWidth
            required
            sx={{ ...inputSx, "& .MuiOutlinedInput-root": { borderRadius: "12px", height: "44px" } }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            size="small"
            fullWidth
            multiline
            rows={2}
            sx={inputSx}
          />
          <TextField
            label="Parent Category"
            value={form.parent}
            onChange={(e) => setForm({ ...form, parent: e.target.value })}
            size="small"
            fullWidth
            select
            sx={{ ...inputSx, "& .MuiOutlinedInput-root": { borderRadius: "12px", height: "44px" } }}
          >
            <MenuItem value="">— None —</MenuItem>
            {categories
              .filter((c) => !editing || c._id !== editing._id)
              .map((c) => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
          </TextField>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 1.5,
              py: 1.25,
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              height: "44px",
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "Heebo", color: "#6B7280", flex: 1 }}>
              Color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "Heebo", color: "#362B24", letterSpacing: "0.06em", fontWeight: 500, fontSize: "0.8rem" }}
              >
                {form.color.toUpperCase()}
              </Typography>
              <Box
                component="label"
                htmlFor="cat-color-picker"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: form.color,
                  border: "2px solid #E5E7EB",
                  cursor: "pointer",
                  flexShrink: 0,
                  boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.2)" },
                }}
              />
              <input
                id="cat-color-picker"
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #F0F0F0", display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              borderRadius: "28px", height: "44px", px: 3.5,
              border: "1px solid #E5E7EB", color: "#362B24",
              fontFamily: "Heebo", fontWeight: 500, textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!form.name}
            sx={{
              borderRadius: "28px", height: "44px", px: 3.5,
              background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
              color: "#fff", fontFamily: "Heebo", fontWeight: 500, textTransform: "none",
              boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
              "&:hover": { opacity: 0.9 },
              "&:disabled": { opacity: 0.5, color: "#fff" },
            }}
          >
            Save
          </Button>
        </Box>
      </CustomModal>
    </Box>
  );
}
